import React from 'react';
import { Hidden } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useAction } from 'core/store/store';
import { formatISOShortDate } from 'core/date/date';

import { subscriptionCurrentSelector } from 'state/subscription/selectors';
import { makeSubscriptionCloseBannerAction } from 'state/subscription/actions';

import { Icon } from 'components/Icon/Icon';
import { Text, TextTypography } from 'components/Text/Text';

import { RoutePath } from 'RoutePath';

import { ReactComponent as CloseIcon } from 'assets/icons/main-16/times--16.svg';
import { ReactComponent as RightArrowIcon } from 'assets/icons/web-16/arrow-right--16.svg';
import i18n from '../../../../i18n';

const useStyles = makeStyles(theme => ({
    background: {
        padding: 16,
        paddingTop: 8,
        [theme.breakpoints.up(640)]: {
            padding: 0,
            height: 40,
        },
        background: (props: { renewSub: boolean }) => (props?.renewSub ? '#FFE55F' : '#FFA310'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        marginLeft: 16,
        [theme.breakpoints.up(640)]: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            margin: '0 auto',
            marginLeft: 16,
        },
        [theme.breakpoints.up(1280)]: {
            width: 780,
            margin: '0 auto',
        },
        [theme.breakpoints.up(1920)]: {
            width: 1240,
            margin: '0 auto',
        },
    },
    button: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        height: 24,
        borderRadius: 32,
        paddingRight: 8,
        paddingLeft: 8,
        backgroundColor: 'black',
        [theme.breakpoints.up(640)]: {
            marginLeft: 24,
        },
        width: 136,
    },
    buttonText: {
        color: 'white',
        marginRight: 4,
    },
    textContainer: {
        marginBottom: 8,
        [theme.breakpoints.up(640)]: {
            marginBottom: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
    },
    close: {
        cursor: 'pointer',
        alignSelf: 'flex-start',
        marginLeft: 8,
        [theme.breakpoints.up(600)]: {
            alignSelf: 'center',
            marginRight: 20,
        },
        display: 'flex',
        justifyContent: 'center',
    },
}));

interface VendorBannerProps {
    subDays: number;
    renewDateInFuture: boolean;
    renewSub: boolean;
    lastSubDay: boolean;
}

export const VendorBanner = (props: VendorBannerProps) => {
    const { subDays, renewDateInFuture, renewSub, lastSubDay } = props;
    const { t } = i18n.useTranslation();

    const history = useHistory();
    const action = useAction();
    const currentSubscription = useSelector(subscriptionCurrentSelector);

    const classes = useStyles({ renewSub: renewSub && !lastSubDay });

    return (
        <div className={classes.background}>
            <div className={classes.container}>
                {renewSub ? (
                    <div className={classes.textContainer}>
                        <Text typography={TextTypography.BODY_ACCENT} component={'span'}>
                            {lastSubDay
                                ? `${
                                      currentSubscription?.amount === 0
                                          ? t('vendor:banner.trial')
                                          : t('vendor:banner.paid')
                                  } ${t('vendor:banner.endTomorrow')}`
                                : `${
                                      currentSubscription?.amount === 0
                                          ? t('vendor:banner.trial')
                                          : t('vendor:banner.paid')
                                  } ${t('vendor:banner.endTomorrow', {
                                      date: formatISOShortDate(currentSubscription?.renewalDate),
                                  })}`}
                        </Text>
                        <Hidden only={'sm'}>
                            <Text typography={TextTypography.BODY} component={'span'}>
                                {t('vendor:banner.activateSubscriptionToContinueUsage')}
                            </Text>
                        </Hidden>
                    </div>
                ) : (
                    <div className={classes.textContainer}>
                        <Text typography={TextTypography.BODY_ACCENT} component={'span'}>
                            {t('vendor:banner.movedToBasic')}
                        </Text>
                        <Hidden only={'sm'}>
                            <Text typography={TextTypography.BODY} component={'span'}>
                                {t('vendor:banner.activateSubscription')}
                            </Text>
                        </Hidden>
                    </div>
                )}
                <div className={classes.button} onClick={() => history.push(RoutePath.VENDOR_MENU_SUBSCRIPTION)}>
                    <Text typography={TextTypography.BODY_ACCENT} className={classes.buttonText} component={'span'}>
                        {t('vendor:banner.activate')}
                    </Text>
                    <Icon component={RightArrowIcon} size={16} svgSize={16} color="#fff" />
                </div>
            </div>
            <div className={classes.close} onClick={() => action(makeSubscriptionCloseBannerAction)}>
                {renewSub && <Icon component={CloseIcon} size={16} svgSize={16} />}
            </div>
        </div>
    );
};
